import { eq, identity, iots, option, pipe, tagged } from '@code-expert/prelude';
import type { CourseUrl, SemesterId } from '/imports/domain';
import { courseUrlEq, semesterIdEq } from '/imports/domain';
import type { Page as EnroledPage } from '/imports/ui/pages/enrolled/pages';
import { EnroledPageParams } from '/imports/ui/pages/enrolled/pages';
import type { Page as MyCoursesPage } from '/imports/ui/pages/myCourses/pages';
import { MyCoursesPageParams } from '/imports/ui/pages/myCourses/pages';
import { ExamPageParams } from '/imports/ui/pages/myExams/pages';
import { GroupsPageParams } from '/imports/ui/pages/myGroups/pages';

const pageParams = {
  enroled: EnroledPageParams,
  mygroups: GroupsPageParams,
  mycourses: MyCoursesPageParams,
  myexams: ExamPageParams,
};

export const PageParams = iots.exact(iots.partial(pageParams));

export type PageParams = iots.TypeOf<typeof PageParams>;

/**
 * Utility type that allows us to fold over the individual key-value combinations of `PageParams`.
 */
export type SetPageParams = {
  [K in keyof Required<PageParams>]: tagged.Tagged<K, Required<PageParams>[K]>;
}[keyof PageParams];

export const setPageParamsAdt = tagged.build<SetPageParams>();

interface SemesterAndCourseUrl {
  semester: SemesterId;
  courseUrl: CourseUrl;
}

export const semesterAndCourseUrlEq: eq.Eq<SemesterAndCourseUrl> = eq.struct({
  semester: semesterIdEq,
  courseUrl: courseUrlEq,
});

const mapEquivalentPage =
  <A extends string, B extends string>(map: Partial<Record<A, B>>) =>
  (page: A | undefined): B | undefined =>
    page != null ? map[page] : undefined;

const mapMyCoursesToEnroled = mapEquivalentPage<MyCoursesPage, EnroledPage>({
  exercises: 'exercises',
  codeExamples: 'codeExamples',
});

const mapEnroledToMyCourses = mapEquivalentPage<EnroledPage, MyCoursesPage>({
  exercises: 'exercises',
  codeExamples: 'codeExamples',
});

export const updatePageParams =
  (params: SetPageParams) =>
  (existing: PageParams | undefined): PageParams => {
    const getPageParams = <K extends keyof PageParams>(
      key: K,
    ): option.Option<Required<PageParams>[K]> =>
      pipe(
        option.fromNullable(existing),
        option.chainNullableK((s) => s[key]),
      );

    /**
     * Update the page params for the given key.
     * If the semester and/or course URL changes, we reset all additional page parameters.
     */
    const update =
      <K extends keyof PageParams>(
        key: K,
        changed?: Omit<PageParams[K], keyof SemesterAndCourseUrl>,
      ) =>
      ({ semester, courseUrl }: SemesterAndCourseUrl): Required<PageParams>[K] =>
        pipe(
          getPageParams(key),
          option.filter(eq.isEqualTo(semesterAndCourseUrlEq)({ semester, courseUrl })),
          option.fold(
            () => ({ semester, courseUrl }),
            (existing) => ({ ...existing, ...changed }),
          ),
        );

    const updateEnroled = (page?: EnroledPage) => update('enroled', { page });

    const updateMyGroups = update('mygroups');

    const updateMyCourses = (page?: MyCoursesPage) => update('mycourses', { page });

    const updateExams = update('myexams');

    return {
      enroled: setPageParamsAdt.fold(params, {
        enroled: identity,
        mygroups: updateEnroled(),
        mycourses: ({ semester, courseUrl, page }) =>
          updateEnroled(mapMyCoursesToEnroled(page))({ semester, courseUrl }),
        myexams: updateEnroled(),
      }),
      mygroups: setPageParamsAdt.fold(params, {
        enroled: updateMyGroups,
        mygroups: identity,
        mycourses: updateMyGroups,
        myexams: updateMyGroups,
      }),
      mycourses: setPageParamsAdt.fold(params, {
        enroled: ({ semester, courseUrl, page }) =>
          updateMyCourses(mapEnroledToMyCourses(page))({ semester, courseUrl }),
        mygroups: updateMyCourses(),
        mycourses: identity,
        myexams: updateMyCourses(),
      }),
      myexams: setPageParamsAdt.fold(params, {
        enroled: updateExams,
        mygroups: updateExams,
        mycourses: updateExams,
        myexams: identity,
      }),
    };
  };
